import { useCallback, useMemo, useState } from 'react';
import { usePermissions } from '@/features/auth';
import type { DealerPeriodStatement } from '../types/dealerFundStatementPeriod.types';
import { canOwnChildren } from '../utils/dealerParentageRules';
import { describePeriod, formatStatementDates, summaryLine } from '../utils/fundStatementLabels';
import { groupStatementBlocks, type StatementLayout } from '../utils/statementBlocks';
import { useFundAccountsSection, type FundAccountCardModel, type FundAccountsModel } from './useFundAccountsSection';
import { useDealerPeriodStatementQuery } from './useDealerPeriodStatementQuery';
import { useDealerQuery } from './useDealerQuery';
import { useStatementPeriod, type RangeDraft, type StatementPeriodMode } from './useStatementPeriod';

export type RollUpState = 'loading' | 'error' | 'unverified' | 'not_entitled' | 'ok';

export interface PeriodHeaderModel {
    mode: StatementPeriodMode;
    canChooseRange: boolean;
    month: string;
    monthOptions: { value: string; label: string }[];
    rangeDraft: RangeDraft;
    rangeError: string | null;
    todayIso: string;
    onModeChange: (mode: StatementPeriodMode) => void;
    onMonthChange: (month: string) => void;
    onRangeDraftChange: (draft: RangeDraft) => void;
    onApplyRange: () => void;
}

export interface RollUpModel {
    state: RollUpState;
    periodLabel: string;
    datesLine: string | null;
    summary: string;
    statement: DealerPeriodStatement | null;
    layout: StatementLayout | null;
    onRetry: () => void;
}

export interface FundStatementPeriodViewModel {
    header: PeriodHeaderModel;
    rollUp: RollUpModel;
    fundAccounts: FundAccountsModel;
    card: FundAccountCardModel;
}

export function useFundStatementPeriodSection(dealerId: string): FundStatementPeriodViewModel {
    const { hasPermission } = usePermissions();
    const canChooseRange = hasPermission({ resource: 'ledger', action: 'fund-statement.date-range' });
    const state = useStatementPeriod(canChooseRange);
    const query = useDealerPeriodStatementQuery(dealerId, state.period, state.todayIso, true);
    const dealer = useDealerQuery(dealerId).data;
    const result = query.data;
    const statement = result?.status === 'ok' ? result.statement : null;
    const layout = useMemo(
        () => (statement ? groupStatementBlocks(statement.rows, statement.unassigned) : null),
        [statement],
    );
    const { refetch } = query;
    const onRetry = useCallback(() => { void refetch({ cancelRefetch: false }); }, [refetch]);

    const { accounts, card, clearSelection } = useFundAccountsSection(dealerId, statement, state.period, state.todayIso);
    const [rangeWasAllowed, setRangeWasAllowed] = useState(canChooseRange);
    if (rangeWasAllowed !== canChooseRange) {
        setRangeWasAllowed(canChooseRange);
        if (!canChooseRange && state.mode === 'range') clearSelection();
    }
    const { setMode, selectMonth, applyRange } = state;
    const onModeChange = useCallback((mode: StatementPeriodMode) => { clearSelection(); setMode(mode); }, [clearSelection, setMode]);
    const onMonthChange = useCallback((month: string) => { clearSelection(); selectMonth(month); }, [clearSelection, selectMonth]);
    const onApplyRange = useCallback(() => { clearSelection(); applyRange(); }, [clearSelection, applyRange]);

    const includesOwnedDealers = dealer ? canOwnChildren(dealer.type ?? 'dealer') : false;
    return {
        header: {
            mode: state.mode,
            canChooseRange,
            month: state.month,
            monthOptions: state.monthOptions,
            rangeDraft: state.rangeDraft,
            rangeError: state.rangeError,
            todayIso: state.todayIso,
            onModeChange,
            onMonthChange,
            onRangeDraftChange: state.setRangeDraft,
            onApplyRange,
        },
        rollUp: {
            state: rollUpState(query.isLoading, query.isError, result?.status),
            periodLabel: describePeriod(state.period, state.todayIso),
            datesLine: statement ? formatStatementDates(statement.from, statement.to) : null,
            summary: summaryLine(dealer?.dealer_code ?? null, includesOwnedDealers),
            statement,
            layout,
            onRetry,
        },
        fundAccounts: accounts,
        card,
    };
}

function rollUpState(isLoading: boolean, isError: boolean, status: string | undefined): RollUpState {
    if (isLoading) return 'loading';
    if (isError) return 'error';
    if (status === 'unverified' || status === 'not_entitled') return status;
    return status === 'ok' ? 'ok' : 'loading';
}
